import { RegionalRates } from "../types/tender.types";

export const UNIT_MAP: Record<string, string> = {
  m2: 'm²', m3: 'm³',
  nr: 'Nr', item: 'Item', wks: 'Wks', week: 'Wks',
  days: 'Days', day: 'Day', hrs: 'Hrs', lm: 'Lm',
  sum: 'Sum', plot: 'Plot', visits: 'Visits',
};

/** Normalise a unit string: superscript m2/m3, consistent capitalisation. */
export const formatUnit = (unit: string | undefined | null): string => {
  if (!unit) return '';
  return UNIT_MAP[unit.trim().toLowerCase()] ?? unit;
};

// Brand Colors
export const COLORS = {
  primary: "#167EF9",
  primaryLight: "#D0EEFE",
  tableHeaderBg: "#F1F3F9",
} as const;

/**
 * UI options for the resource-code combine operator shown in the Add/Edit
 * Item modal's "Multiple" code mode (Dynamic Bill Summary tabs and every
 * Bill Summary's own ad-hoc extra items — DynamicBillSummaryPanel.tsx,
 * useBillSummaryExtraItems.tsx) — was duplicated as an inline `options={[...]}`
 * array in both places. Centralised here so a future operator (e.g. `×`/`÷`)
 * only needs adding in one spot. The underlying `operator` field itself
 * stays `'+' | '-'` (`DynamicBillSummaryResourceCodeEntry`, tender.types.ts)
 * since the combine math already supports both — this list only controls
 * which ones are actually offered in the dropdown; only `+` for now.
 */
export const RESOURCE_CODE_OPERATOR_OPTIONS: { value: '+' | '-'; label: string }[] = [
  { value: '+', label: '+' },
  { value: '-', label: '-' },
];

// Dynamic Bill Summary / ad-hoc extra-items sections may nest up to
// parent -> child -> child (depths 0, 1, 2) — shared by DynamicBillSummaryPanel.tsx
// and every groundworks Bill Summary's "Add Section" Actions icon, so a 4th
// level can never be created in either place.
export const MAX_SECTION_NESTING_DEPTH = 2;
export const MAX_SECTION_NESTING_DEPTH_MESSAGE = "Depth up to two children sections allowed";

/** Generic tree-depth walker for any row shape with `key`/`children` (e.g. a
 *  Bill Summary's merged `processedData`/`collectiveDisplayData`, which mixes
 *  real, non-dynamically-created category rows with grafted ad-hoc extra
 *  rows in one `children`-nested tree). Root rows get depth 0, their
 *  children depth 1, grandchildren depth 2, etc. — used against
 *  `MAX_SECTION_NESTING_DEPTH` to disable a row's "Add Section" Actions icon
 *  once nesting bottoms out, counting BOTH pre-existing real category
 *  nesting and any already-added extra sections, not just the latter. */
export const buildSectionDepthMap = <T extends { key: string; children?: T[] }>(
  rows: T[],
): Map<string, number> => {
  const map = new Map<string, number>();
  const walk = (nodes: T[], depth: number) => {
    nodes.forEach((node) => {
      map.set(node.key, depth);
      if (node.children && node.children.length > 0) walk(node.children, depth + 1);
    });
  };
  walk(rows, 0);
  return map;
};

export const regionalMultipliers: RegionalRates = {
  "East Midlands": { labour: 0.97, materials: 0.98 },
  "Eastern Counties": { labour: 1.0, materials: 1.0 },
  "North West": { labour: 0.93, materials: 0.96 },
  "South West": { labour: 1.0, materials: 1.0 },
  Southern: { labour: 1.1, materials: 1.05 },
  "West Midlands": { labour: 1.0, materials: 1.0 },
  Yorkshire: { labour: 1.0, materials: 1.0 },
};

export const REGIONS = [
  "East Midlands",
  "Eastern Counties",
  "North West",
  "South West",
  "Southern",
  "West Midlands",
  "Yorkshire",
] as const;

export const REGION_TO_BC_CODE_MAP: Record<string, string> = {
  "East Midlands": "EAST MIDS",
  "Eastern Counties": "EAST COUNT",
  "North West": "NORTH WEST",
  "South West": "SOUTH WEST",
  Southern: "SOUTHERN",
  "West Midlands": "WEST MIDS",
  Yorkshire: "YORKSHIRE",
};

export const PIPE_TYPES = [
  { value: "clay", label: "Clay " },
  { value: "plastic", label: "Plastic " },
  { value: "concrete", label: "Concrete (Precast Concrete)" },
] as const;

export const BACKFILL_TYPES = [
  { value: "type1", label: "Type 1" },
  { value: "6F2",   label: "R/C 6F2" },
  { value: "6F5",   label: "Q 6F5" },
  { value: "asDug", label: "As Dug" },
] as const;

export const FOUL_MANHOLE_DIAMETERS = [
  "1200mm",
  "1350mm",
  "1500mm",
  "1800mm",
  "2100mm",
  "2400mm",
  "2700mm",
  "3000mm",
  "3600mm",
  "NME PPIC",
] as const;

export const STORM_MANHOLE_DIAMETERS = [
  "1200mm",
  "1350mm",
  "1500mm",
  "1800mm",
  "2100mm",
  "2400mm",
  "2700mm",
  "3000mm",
  "3600mm",
  "NME PPIC",
] as const;

// Unit conversion constants and utilities
export const UNIT_CONVERSIONS = {
  SQ_FT_TO_SQ_M: 0.092903, // 1 square foot = 0.092903 square meters
  SQ_M_TO_SQ_FT: 10.7639, // 1 square meter = 10.7639 square feet
} as const;

/**
 * Convert square feet to square meters
 * @param sqFt - Value in square feet
 * @returns Value in square meters, formatted to 2 decimal places
 */
export const convertSqFtToSqM = (sqFt: string | number | null): string => {
  if (!sqFt || sqFt === "" || sqFt === null) return "";
  const sqFtValue = typeof sqFt === "string" ? parseFloat(sqFt) : sqFt;
  if (isNaN(sqFtValue)) return "";
  const sqM = sqFtValue * UNIT_CONVERSIONS.SQ_FT_TO_SQ_M;
  return sqM.toFixed(2);
};

/**
 * Convert square meters to square feet
 * @param sqM - Value in square meters
 * @returns Value in square feet, formatted to 2 decimal places
 */
export const convertSqMToSqFt = (sqM: string | number | null): string => {
  if (!sqM || sqM === "" || sqM === null) return "";
  const sqMValue = typeof sqM === "string" ? parseFloat(sqM) : sqM;
  if (isNaN(sqMValue)) return "";
  const sqFt = sqMValue * UNIT_CONVERSIONS.SQ_M_TO_SQ_FT;
  return sqFt.toFixed(2);
};

export const FOUL_PIPE_DIAMETERS = [
  "150mm",
  "225mm",
  "300mm",
  "450mm",
  "600mm",
  "750mm",
  "900mm",
  "1200mm",
  "1500mm",
  "1800mm",
  "2100mm",
  "2400mm",
] as const;

export const STORM_PIPE_DIAMETERS = [
  "150mm",
  "225mm",
  "300mm",
  "375mm",
  "450mm",
  "525mm",
  "600mm",
  "750mm",
  "900mm",
  "1200mm",
  "1500mm",
  "1800mm",
  "2100mm",
  "2400mm",
] as const;

// Roads constants
export const TARMAC_SURFACE_COURSE_TYPES = [
  "AC10",
  "AC14",
  "HRA 55/10",
  "HRA 55/14",
  "SMA",
] as const;

/** Surface course groups for the Road Types table's Surface selector; each filters the Tarmac cells' dropdown to `SURFACE_COURSE_OPTIONS_BY_GROUP[group]`. */
export const SURFACE_COURSE_GROUPS = ["HRA", "DB", "SMA"] as const;

export const SURFACE_COURSE_OPTIONS_BY_GROUP = {
  HRA: [
    { c: "SCSTC006A", n: "14mm HRA - A", r: 22.5 },
    { c: "SCSTC006B", n: "14mm HRA - B", r: 22.5 },
    { c: "SCSTC006C", n: "14mm HRA - C", r: 22.5 },
    { c: "SCSTC006D", n: "14mm HRA - D", r: 22.5 },
    { c: "SCSTC006E", n: "14mm HRA - E", r: 22.5 },
    { c: "SCSTC006F", n: "14mm HRA - F", r: 22.5 },
  ],
  DB: [
    { c: "SCSTC007A", n: "6mm SMA - A", r: 20 },
    { c: "SCSTC007B", n: "6mm SMA - B", r: 20 },
    { c: "SCSTC007C", n: "6mm SMA - C", r: 20 },
  ],
  SMA: [
    { c: "SCSTC008A", n: "10mm SMA - A", r: 20 },
    { c: "SCSTC008B", n: "10mm SMA - B", r: 20 },
    { c: "SCSTC008C", n: "10mm SMA - C", r: 20 },
  ],
} as const;

export const BLOCK_PAVED_SURFACE_COURSE_TYPES = [
  "200x100x60mm Block Paving",
  "200x100x80mm Block Paving",
  "60mm Tegula Paving",
  "80mm Tegula Paving",
  "200x100x60mm Keybloc Paving",
  "200x100x80mm Keybloc Paving",
] as const;

export const SURFACE_COURSE_TYPES = TARMAC_SURFACE_COURSE_TYPES;

export const SUB_BASE_TYPES = ["Type 1", "6F2"] as const;

export const CAPPING_TYPES = ["R/C 6F2 capping", "Q 6F5 capping", "W75/W150"] as const;

/** Selectable Base (base course) materials for the Carriageway Takeoff Road Types table — `n` is shown in the dropdown and the Bill Summary description, `c` is its item-library code. */
export const BASE_COURSE_OPTIONS = [
  { c: "SCSTC001A", n: "AC32 - A" },
  { c: "SCSTC001B", n: "AC32 - B" },
  { c: "SCSTC001C", n: "AC32 - C" },
] as const;

/** Optional items "attached" to the Base course (Carriageway Takeoff Road Types table). Multi-select; `NONE` is legacy (the "Neither" option was removed; saved values are ignored). */
export const BASE_ATTACHMENT_OPTIONS = [
  { c: "SCPRE007", n: "Binder & grading analysis" },
  { c: "SCPRE008", n: "NDT's" },
] as const;
export const BASE_ATTACHMENT_NONE = "NONE" as const;

/** Description suffix for the Base course row, e.g. ", attached Binder & grading analysis - NDT's" (empty when none selected). */
export const formatBaseAttachments = (selected: readonly string[] | undefined): string => {
  const names = BASE_ATTACHMENT_OPTIONS.filter((o) => selected?.includes(o.c)).map((o) => o.n);
  return names.length > 0 ? `, attached ${names.join(' - ')}` : '';
};

/** Selectable Binder (binder course) materials for the Carriageway Takeoff Road Types table — same shape/usage as BASE_COURSE_OPTIONS. */
export const BINDER_COURSE_OPTIONS = [
  { c: "SCSTC002A", n: "AC20 - A" },
  { c: "SCSTC002B", n: "AC20 - B" },
  { c: "SCSTC002C", n: "AC20 - C" },
] as const;

export const CAPPING_LAYER_THICKNESSES = [350, 450, 600] as const;

export const DEFAULT_ROAD_CONSTRUCTION = {
  surface: "",
  surfaceCourse: { type: "HRA 55/10" as const, thickness: 0 },
  binderCourse: { thickness: 0 },
  baseCourse: { thickness: 0 },
  subBase: { type: "Type 1" as const, thickness: 0 },
  capping: 0,
};

export const PRESET_ROAD_TYPES = {
  TARMAC_ROAD: {
    name: "Tarmac Road",
    surfaceCourse: { type: "HRA 55/10" as const, thickness: 40 },
    binderCourse: { thickness: 60 },
    baseCourse: { thickness: 150 },
    subBase: { type: "Type 1" as const, thickness: 150 },
  },
  BLOCK_PAVED_ROAD: {
    name: "Block Paved Road",
    surfaceCourse: { type: "SMA 10" as const, thickness: 80 },
    binderCourse: { thickness: 50 },
    baseCourse: { thickness: 150 },
    subBase: { type: "Type 1" as const, thickness: 200 },
  },
} as const;

// Backwards compatibility
export const MANHOLE_DIAMETERS = FOUL_MANHOLE_DIAMETERS;
export const PIPE_DIAMETERS = FOUL_PIPE_DIAMETERS;

export const MNJLOGOBASE64 = `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`;

export type EvaluateCode =
  | "ALP041"
  | "ALP042"
  | "ALP043"
  | "ALP044"
  | "ALP045"
  | "ALP046"
  | "ALP047"
  | "ALP048"
  | "ALP049"
  | "ALP051"
  | "ALP050"
  | "ALP052"
  | "ALP053"
  | "GWALPD001"
  | "GWALPD002"
  | "GWALPD003"
  | "GWALPD004"
  | "GWALPD005"
  | "GWALPD006"
  | "GWALPD007"
  | "GWALPD008"
  | "GWALPD009"
  | "GWALPD010"
  | "GWALP007"
  | "GWALP008"
  | "GWALP040"
  | "GWALP041"
  | "GWALP042"
  | "GWALP043"
  | "GWALP044"
  | "GWALP045"
  | "GWALPDD020"
  | "GWALPDD059"
  | "GWALPDD060"
  | "GWALPDD056"
  | "GWALPDD057"
  | "L&P3"
  | "GWALPF005"
  | "GWALPF006"
  | "GWALPF007"
  | "GWALPF008"
  | "GWALPF009"
  | "GWALPF010"
  | "GWALPF011"
  | "GWALPF012"
  | "GWALPF014"
  | "GWALPF015"
  | "GWALPF017"
  | "GWALPF018"
  | "GWALPF019"
  | "GWALPF020"
  | "GWALPF021";

interface EvaluateCodeEntry {
  code: EvaluateCode;
  label: string;
  thicknessMm: number;
  aggregateSizeMm: number;
}

export const EVALUATE_CODES: Record<EvaluateCode, EvaluateCodeEntry> = {
  ALP041: {
    code: "ALP041",
    label: "100mm thick AC 32mm dense bin",
    thicknessMm: 100,
    aggregateSizeMm: 32,
  },
  ALP042: {
    code: "ALP042",
    label: "150mm thick AC 32mm dense bin",
    thicknessMm: 150,
    aggregateSizeMm: 32,
  },
  ALP043: {
    code: "ALP043",
    label: "50mm thick AC 20mm dense bin",
    thicknessMm: 50,
    aggregateSizeMm: 20,
  },
  ALP044: {
    code: "ALP044",
    label: "60mm thick AC 20mm dense bin",
    thicknessMm: 60,
    aggregateSizeMm: 20,
  },
  ALP045: {
    code: "ALP045",
    label: "75mm thick AC 20mm dense bin",
    thicknessMm: 75,
    aggregateSizeMm: 20,
  },
  ALP046: {
    code: "ALP046",
    label: "80mm thick AC 20mm dense bin",
    thicknessMm: 80,
    aggregateSizeMm: 20,
  },
  ALP047: {
    code: "ALP047",
    label: "100mm thick AC 20mm dense bin",
    thicknessMm: 100,
    aggregateSizeMm: 20,
  },
  ALP048: {
    code: "ALP048",
    label: "20mm thick AC 6mm dense surf",
    thicknessMm: 20,
    aggregateSizeMm: 6,
  },
  ALP049: {
    code: "ALP049",
    label: "25mm thick AC 6 or 10mm dense surf",
    thicknessMm: 25,
    aggregateSizeMm: 6,
  },
  ALP051: {
    code: "ALP051",
    label: "30mm thick AC 6 or 10mm dense surf",
    thicknessMm: 30,
    aggregateSizeMm: 6,
  },
  ALP052: {
    code: "ALP052",
    label: "25 or 30mm thick 6 or 10mm SMA",
    thicknessMm: 25,
    aggregateSizeMm: 6,
  },
  ALP053: {
    code: "ALP053",
    label: "25 or 30mm thick 6 or 10mm HRA",
    thicknessMm: 25,
    aggregateSizeMm: 6,
  },
  GWALPD001: {
    code: "GWALPD001",
    label: "200x100x80mm Charcon Europa paving on 50mm sand bed",
    thicknessMm: 80,
    aggregateSizeMm: 0,
  },
  GWALPD002: {
    code: "GWALPD002",
    label: "200x100x60mm Charcon Europa paving on 50mm sand bed",
    thicknessMm: 60,
    aggregateSizeMm: 0,
  },
  GWALPD003: {
    code: "GWALPD003",
    label: "200x100x80mm Charcon Infilta paving",
    thicknessMm: 80,
    aggregateSizeMm: 0,
  },
  GWALPD004: {
    code: "GWALPD004",
    label: "200x100x60mm Charcon Infilta paving",
    thicknessMm: 60,
    aggregateSizeMm: 0,
  },
  GWALPD005: {
    code: "GWALPD005",
    label: "200x100x80mm Marshalls Tegula paving on 50mm sand bed",
    thicknessMm: 80,
    aggregateSizeMm: 0,
  },
  GWALPD006: {
    code: "GWALPD006",
    label: "200x100x60mm Marshalls Tegula paving on 50mm sand bed",
    thicknessMm: 60,
    aggregateSizeMm: 0,
  },
  GWALPD007: {
    code: "GWALPD007",
    label: "200x100x80mm Marshalls Priora paving",
    thicknessMm: 80,
    aggregateSizeMm: 0,
  },
  GWALPD008: {
    code: "GWALPD008",
    label: "200x100x60mm Marshalls Priora paving",
    thicknessMm: 60,
    aggregateSizeMm: 0,
  },
  GWALPD009: {
    code: "GWALPD009",
    label: "200x100x80mm Marshalls Keyblock paving on 50mm sand bed",
    thicknessMm: 80,
    aggregateSizeMm: 0,
  },
  GWALPD010: {
    code: "GWALPD010",
    label: "200x100x60mm Marshalls Keyblock paving on 50mm sand bed",
    thicknessMm: 60,
    aggregateSizeMm: 0,
  },
  GWALP007: {
    code: "GWALP007",
    label: "Plain Grey slabs on 50mm sand beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP008: {
    code: "GWALP008",
    label: "Buff / Grey riven slabs on 50mm sand beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP040: {
    code: "GWALP040",
    label: "Plain Grey Slabs 450x450 on 50mm beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP041: {
    code: "GWALP041",
    label: "Torver Buff Textured slabs 600x600 on 50mm beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP042: {
    code: "GWALP042",
    label: "Marshalls Textured Utility slabs 450x450 on 50mm beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP043: {
    code: "GWALP043",
    label: "Marshalls Pendle Buff Riven slabs 600x600 on 50mm beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP044: {
    code: "GWALP044",
    label: "Marshalls Saxon Buff Riven slabs 600x600 on 50mm beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALP045: {
    code: "GWALP045",
    label: "Bradstone Textured Buff slabs 450x450 on 50mm beds",
    thicknessMm: 50,
    aggregateSizeMm: 0,
  },
  GWALPDD020: {
    code: "GWALPDD020",
    label: "Drainage channel",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPDD059: {
    code: "GWALPDD059",
    label: "A15 Drainage channel",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPDD060: {
    code: "GWALPDD060",
    label: "B125 Drainage channel",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPDD056: {
    code: "GWALPDD056",
    label: "Land drainage",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPDD057: {
    code: "GWALPDD057",
    label: "Connection to drainage system",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  "L&P3": {
    code: "L&P3",
    label: "Silt trap chamber",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF005: {
    code: "GWALPF005",
    label: "Concrete beam & block floor; 225mm Thermal Economics Insulation; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres - B&B houses",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF006: {
    code: "GWALPF006",
    label: "Garage - Beam and block",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF007: {
    code: "GWALPF007",
    label: "Concrete beam & polystyrene infill-panel floor (including top sheet insulation) to achieve 0.15 U-value; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres - B&B houses",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF008: {
    code: "GWALPF008",
    label: "Concrete beam & polystyrene infill-panel floor (including top sheet insulation) to achieve 0.13 U-value; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres - B&B houses",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF009: {
    code: "GWALPF009",
    label: "Concrete beam & polystyrene infill-panel floor (including top sheet insulation) to achieve 0.11 U-value; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres - B&B houses",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF010: {
    code: "GWALPF010",
    label: "Concrete beam & block floor; 175mm Jablite Insulation; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres - B&B houses",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF011: {
    code: "GWALPF011",
    label: "150mm thick cast insitu suspended slab 150mm thick; 150mm Kingspan TF70 Insulation laid on 50mm sand bed - houses",
    thicknessMm: 150,
    aggregateSizeMm: 0,
  },
  GWALPF012: {
    code: "GWALPF012",
    label: "Cast Insitu Slab",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF014: {
    code: "GWALPF014",
    label: "Supply and lay composite Jetfloor, 150mm concrete beams; 1200g membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - Countryside Homes",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF015: {
    code: "GWALPF015",
    label: "Supply and lay composite Jetfloor, 150mm concrete beams; 1200g visqueen membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - Redrow Homes",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF017: {
    code: "GWALPF017",
    label: "Supply and lay McCann 150mm concrete beams and block floor; 1200g membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - Vistry Homes (group Deal)",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF018: {
    code: "GWALPF018",
    label: "Supply and lay Longley 150mm concrete beams and block floor; 1200g membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - Avant, Morris Homes",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF019: {
    code: "GWALPF019",
    label: "Supply and lay Litecast XT 150mm concrete beams and block floor; 1200g membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - St Modwen",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF020: {
    code: "GWALPF020",
    label: "Supply and lay Jablite all in one thermal floor; 1200g membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - Davidsons Homes",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
  GWALPF021: {
    code: "GWALPF021",
    label: "Supply and lay composite Jetfloor, 150mm concrete beams; 1200g visqueen membrane; 75mm concrete screed with Durus macro-fibres;minimum 250mm void. - B&B houses - Miller Homes",
    thicknessMm: 0,
    aggregateSizeMm: 0,
  },
};

export const UK_REGIONS_TO_CODE_MAP: Record<string, string> = {
  "West Midlands": "10",
  "East Midlands": "15",
  Southern: "20",
  "North West": "30",
  Yorkshire: "40",
  "South West": "50",
  "Eastern Counties": "60",
};

export const UK_REGIONS = Object.keys(UK_REGIONS_TO_CODE_MAP);

// Canonical project-team role labels. Mirrors MJ-Extended-PM/MJ-Tracker's
// USER_ROLES (@mj/shared-constants) — kept as a local literal here since this
// app isn't part of that Nx workspace.
export const USER_ROLES = {
  BOARD_LEVEL: "Board Level",
  REGIONAL_MD: "Regional MD",
  SENIOR_ESTIMATOR: "Senior Estimator",
  COMMERCIAL_DIRECTOR: "Commercial Director",
  COMMERCIAL_MANAGER: "Commercial Manager",
  CONTRACTS_MANAGER: "Contracts Manager",
  QS_QUANTITY_SURVEYOR: "QS (Quantity Surveyor)",
  PROJECT_MANAGER: "Project Manager",
  ESTIMATOR: "Estimator",
  SITE_MANAGER_SUPERVISOR: "Site Manager/Supervisor",
  CONTRACTS_DIRECTOR: "Contracts Director",
  SYSTEM_ADMINISTRATOR: "System Administrator",
};
